import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { createDiscountsSchema } from '../../assets/schemas/schemas';
import { initialPager } from '../../components/views/Parameters/parametersConstants';
import { discountService } from '../../services/discountService';
import { notify } from '../../assets/helpers/toast';
import { useForm } from 'react-hook-form';
import { inactiveRowCount } from '../../components/views/Parameters/parametersTableUtils';

export const LANDING_DISCOUNT_CAMPAIGN_STATUS = {
  CREADA: 'Creada',
  ACTIVO: 'Activo',
  VENCIDO: 'Vencido',
  INACTIVO: 'Inactivo',
}

export default function useDiscount() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
    control,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(createDiscountsSchema()),
    defaultValues: {
      discountCode: '',
      description: '',
      landings: [],
      startDate: '',
      expirationDate: '',
    },
  });

  const [showDiscountForm, setShowDiscountForm] = useState(false);

  const [discountData, setDiscountData] = useState({
    limit: initialPager.limit,
    offset: 0,
    data: [],
    count: 0,
    isLoading: false,
  });

  const [editDiscountId, setEditDiscountId] = useState(null);
  const [listActiveCodes, setListActiveCodes] = useState([]);

  const handleDiscountPageChange = useCallback((_e, offset) => {
    setDiscountData((prev) => ({
      ...prev,
      offset: (offset - 1) * prev.limit,
    }));
  }, []);

  const handleToggleDiscountActive = useCallback(async (row) => {
    const next =
      row.status === LANDING_DISCOUNT_CAMPAIGN_STATUS.ACTIVO
        ? LANDING_DISCOUNT_CAMPAIGN_STATUS.INACTIVO
        : LANDING_DISCOUNT_CAMPAIGN_STATUS.ACTIVO;

    const response = await discountService.updateDiscount(row._id, {
      status: next,
    });

    if ([200, 201].includes(response?.status)) {
      notify(next, 'success');
      setDiscountData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === row._id ? { ...item, status: next } : item
        ),
      }));
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al actualizar el estado del modelo',
        'error'
      );
    }
  }, []);

  const handleEditDiscount = useCallback(
    (row) => {
      setShowDiscountForm(true);
      setEditDiscountId(row._id);
      reset({
        discountCode: row.discountCode?._id,
        description: row.description,
        landings: row.landings,
        startDate: row.startDate ? new Date(row.startDate) : '',
        expirationDate: row.expirationDate ? new Date(row.expirationDate) : '',
        status: row.status,
      });
    },
    [reset]
  );

  const onDiscountSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setDiscountData((prev) => ({
      ...prev,
      offset: 0,
      limit: value,
    }));
  };

  const fetchDiscounts = useCallback(
    async (abortSignal) => {
      setDiscountData((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await discountService.getDiscounts({
          offset: discountData.offset,
          limit: discountData.limit,
        });

        if (abortSignal?.aborted) return;

        if ([200, 201].includes(response?.status)) {
          const rows =
            response?.data?.campaigns?.map((discount) => ({
              ...discount,
            })) || [];

          setDiscountData((prev) => ({
            ...prev,
            data: rows,
            count: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar la lista de descuentos',
            'error'
          );
          setDiscountData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        if (abortSignal?.aborted) return;
        notify('Error al cargar la lista de descuentos', 'error');
        setDiscountData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [discountData.offset, discountData.limit]
  );

  const onSubmit = handleSubmit(async (data) => {
    delete data.status; // Elimina el campo de estado antes de enviarlo al backend, ya que no es necesario para la creación o actualización del descuento
    if (editDiscountId) {
      const response = await discountService.updateDiscount(
        editDiscountId,
        {
          ...data,
          startDate: data.startDate ? new Date(data.startDate).toISOString() : '',
          expirationDate: data.expirationDate ? new Date(data.expirationDate).toISOString() : ''
        } // Asegúrate de enviar el ID del canal de venta y las fechas en formato ISO
      );

      if ([200, 201].includes(response?.status)) {
        notify('Recargo actualizado', 'success');
        fetchDiscounts();
        setEditDiscountId(null);
        setShowDiscountForm(false);
        reset({
          discountCode: '',
          description: '',
          landings: [],
          startDate: '',
          expirationDate: '',
        });
      } else {
        notify(
          response?.message ??
          response?.data?.message ??
          'Error al actualizar el descuento',
          'error'
        );
      }
      return;
    }

    const response = await discountService.createDiscount({
      ...data,
      startDate: data.startDate ? new Date(data.startDate).toISOString() : '',
      expirationDate: data.expirationDate ? new Date(data.expirationDate).toISOString() : ''
    });

    if ([200, 201].includes(response?.status)) {
      notify('Descuento creado', 'success');
      reset({
        discountCode: '',
        description: '',
        landings: [],
        startDate: '',
        expirationDate: '',
      });
      setShowDiscountForm(false);
      fetchDiscounts();
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al crear el recargo',
        'error'
      );
    }
  });

  const closeForm = () => {
    setShowDiscountForm(false);
    setEditDiscountId(null);
    reset({ discountCode: '', description: '', landings: [], startDate: '', expirationDate: '' });
  }

  useEffect(() => {
    const abortController = new AbortController();
    fetchDiscounts(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchDiscounts]);

  const inactiveDiscount = inactiveRowCount(discountData.data);

  const clearErrorByFieldName = (fieldName) => {
    setError(fieldName, null);
    clearErrors(fieldName);
  }

  const getActiveCodes = useCallback(async (isOpen) => {
    if (!isOpen) {
      return [];
    }

    try {
      const response = await discountService.getActiveCodes({
        limit: 100,
        offset: 0,
        status: 'Activo',
      });

      if ([200, 201].includes(response?.status)) {
        const activeCodes = response?.data?.discounts || [];
        setListActiveCodes(activeCodes.map((code) => ({ name: code.code, _id: code._id })));
      } else {
        notify(
          'Error al cargar los códigos de descuento activos',
          'error'
        );
      }
    } catch (error) {
      setListActiveCodes([]);
      notify(
        'Error al cargar los códigos de descuento activos',
        'error'
      );
    }
  }, []);

  useEffect(() => {
    getActiveCodes(showDiscountForm);
  }, [showDiscountForm, getActiveCodes]);

  return {
    register,
    handleSubmit,
    reset,
    errors,
    showDiscountForm,
    setShowDiscountForm,
    discountData,
    setDiscountData,
    editDiscountId,
    setEditDiscountId,
    handleDiscountPageChange,
    handleToggleDiscountActive,
    handleEditDiscount,
    onSubmit,
    onDiscountSelectPerPage,
    inactiveDiscount,
    closeForm,
    setValue,
    getValues,
    control,
    clearErrorByFieldName,
    getActiveCodes,
    listActiveCodes
  };
}
